// src/components/EditProfileModal.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from "@mui/material";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../@shared/firebase/config";

import ModalComponent from "./ModalComponent";
import NeoButton from "./NeoButton";
import { MainContext } from "../App";
import {
  composeOrbAvatarFromLayers,
  getOrbLayerSourceMeta,
  normalizeOrbRecord,
} from "../@shared/utils/orbAvatar";

import {
  PROFILE_IMAGES,
  BACKGROUND_IMAGES,
} from "../@shared/utils/profileImages";
import { isValidUsername, getBadgeByLevel } from "../@shared/utils/Utils";

const toNumericLevel = (lvl) => {
  if (!lvl) return 0;
  if (typeof lvl === "string" && lvl.startsWith("M")) {
    const tier = parseInt(lvl.slice(1), 10) || 0;
    return 100 + tier;
  }
  return parseInt(lvl, 10) || 0;
};

const GridTile = ({ locked, selected, label, img, onClick }) => (
  <Box
    onClick={() => !locked && onClick?.()}
    sx={{
      position: "relative",
      border: selected ? "3px solid #ffd24d" : "2px solid #0a5cff55",
      borderRadius: 2,
      overflow: "hidden",
      cursor: locked ? "not-allowed" : "pointer",
      boxShadow: selected ? "0 0 12px rgba(255, 210, 77, 0.6)" : "none",
      "&:hover": locked ? {} : { borderColor: "#ffd24d" },
      display: "flex",
      justifyContent: "center",
    }}
  >
    <img
      src={img}
      alt={label || "option"}
      style={{
        width: "85%",
        height: "auto",
        objectFit: "cover",
        display: "block",
      }}
    />
    {label && (
      <Typography
        sx={{
          position: "absolute",
          left: 8,
          bottom: 6,
          color: "#fff",
          fontWeight: 800,
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          fontSize: 12,
        }}
      >
        {label}
      </Typography>
    )}
    {locked && (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 900,
          fontSize: 12,
          letterSpacing: 0.5,
        }}
      >
        🔒 Locked
      </Box>
    )}
  </Box>
);

const ProfileHeader = ({ user }) => {
  const { getBadgeByLevel: _noop } = {}; // silence unused
  const { badge, name } = getBadgeByLevel(user?.level);
  return (
    <Box
      sx={{
        position: "relative",
        p: 2,
        borderRadius: 2,
        background: "linear-gradient(180deg, #2a184f 0%, #231543 100%)",
        border: "2px solid #0a5cff55",
        boxShadow: "inset 0 0 0 2px #140b40, 0 8px 20px rgba(0,0,0,.35)",
        // mb: 2,
        display: "flex",
        gap: 2,
        alignItems: "center",
        width: "100%",
        containerType: "inline-size",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 64,
          height: 64,
          border: "3px solid #3a1fa4",
          boxShadow: "0 0 10px #0a5cff66",
          borderRadius: "8px",
          // overflow: "hidden",
        }}
      >
        <img
          src={user?.imageBg || BACKGROUND_IMAGES[0].img}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "6px",
          }}
        />
        <img
          src={user?.imageUrl || PROFILE_IMAGES[0].img}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "6px",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -8,
            right: -10,
            width: 36,
            height: 36,
          }}
        >
          <img
            src={badge}
            alt="badge"
            style={{
              width: "100%",
              height: "auto",
              filter: "drop-shadow(0 0 6px #0a5cff66)",
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textShadow:
                "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
            }}
          >
            {user?.level}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 900,
            color: "#fff",
            textShadow:
              "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
            fontSize: "6.5cqw",
          }}
        >
          @{user?.username}
        </Typography>
        <Typography sx={{ color: "#c9b7ff", fontSize: "5cqw" }}>
          {name}
        </Typography>

        <Box
          sx={{
            mt: 1,
            width: "90%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#2b1d63",
            p: "6px 10px",
            borderRadius: "8px",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component={"img"}
              sx={{ width: 18, height: 18 }}
              src={require("../@shared/assets/infift_token.png")}
            />
            <Typography sx={{ fontSize: 13, color: "#fff" }}>
              {user?.infinityTokens ?? 0}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component={"img"}
              sx={{ width: 13, height: "auto", rotate: "-15deg" }}
              src={require("../@shared/assets/back-of-card.png")}
            />
            <Typography sx={{ fontSize: 13, color: "#fff" }}>
              {user?.nftCards?.length ?? 0}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              component={"img"}
              sx={{ width: 20, height: 20 }}
              src={require("../@shared/assets/icons/trophy_icon.png")}
            />
            <Typography sx={{ fontSize: 13, color: "#fff" }}>
              {user?.record?.wins ?? 0}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const EditProfileModal = ({ visible, onClose, userData }) => {
  const { Theme, setMessage, setError, setSuccess } = useContext(MainContext);
  const isNarrow = typeof window !== "undefined" ? window.innerWidth < 400 : false;

  // working state
  const [tab, setTab] = useState(0);
  const [username, setUsername] = useState(userData?.username || "");
  const [imageUrl, setImageUrl] = useState(userData?.imageUrl || "");
  const [imageBg, setImageBg] = useState(userData?.imageBg || "");

  const [nameStatus, setNameStatus] = useState("idle"); // idle | checking | ok | taken
  const lowerSelf = (userData?.username || "").toLowerCase();

  const [nameTouched, setNameTouched] = useState(false);

    // Infinity Orbs (owned)
  const [orbItems, setOrbItems] = useState([]); // [{ tokenId, name, img }]
  const [orbsLoading, setOrbsLoading] = useState(false);
  const [orbsLoaded, setOrbsLoaded] = useState(false);
  const [profileImageOptions, setProfileImageOptions] = useState(PROFILE_IMAGES);



  const [saving, setSaving] = useState(false);

  const canLevel = useMemo(
    () => toNumericLevel(userData?.level),
    [userData?.level]
  );

  useEffect(() => {
    let active = true;
    const run = async () => {
      const val = (username || "").trim();
      if (!val || val.toLowerCase() === lowerSelf || !isValidUsername(val)) {
        setNameStatus("idle");
        return;
      }
      setNameStatus("checking");
      // current approach: fetch all (matches your chosen strategy right now)
      const snap = await getDocs(collection(db, "users"));
      if (!active) return;
      const lower = val.toLowerCase();
      const conflict = snap.docs.some(
        (d) =>
          (d.data()?.username || "").toLowerCase() === lower &&
          d.id !== userData.id
      );
      setNameStatus(conflict ? "taken" : "ok");
    };
    const t = setTimeout(run, 500); // debounce
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [username, userData?.id, lowerSelf]);

  useEffect(() => {
    if (visible) {
      setTab(0);
      setUsername(userData?.username || "");
      setImageUrl(userData?.imageUrl || "");
      setImageBg(userData?.imageBg || "");
      setProfileImageOptions(PROFILE_IMAGES);
      setNameTouched(false);
    }
  }, [visible, userData]);

    useEffect(() => {
  if (!visible || !userData?.id) return;

  let alive = true;

  const loadOrbsIntoProfileImages = async () => {
    try {
      setOrbsLoading(true);
      setOrbsLoaded(false);
      const orbsRef = collection(db, "users", userData.id, "InfinityOrbs");
      const snap = await getDocs(query(orbsRef, limit(100)));

      if (!alive) return;

      const orbImages = snap.docs
        .map((d) => {
          const data = d.data() || {};
          if (!data.imageUrl) return null;

          return {
            img: data.imageUrl,
            name: data.name || `Infinity Orb #${Number(d.id) + 1}`,
            level: 0, // 🔓 always unlocked
            source: "orb", // optional: helpful for debugging / styling later
            tokenId: d.id, // optional
          };
        })
        .filter(Boolean);

      // 🔥 Prepend orbs to the default PROFILE_IMAGES
      setProfileImageOptions([
        ...orbImages,
        ...PROFILE_IMAGES,
      ]);

      const orbRecords = snap.docs
        .map((d) => normalizeOrbRecord({ tokenId: d.id, ...d.data() }))
        .filter((orb) => orb?.imageUrl || orb?.composedImage);

      setOrbItems(orbRecords);

      const composedOrbImages = (
        await Promise.all(
          orbRecords.map(async (orb) => {
            const layerSourceMeta = getOrbLayerSourceMeta(orb);
            const fallbackImage = orb.composedImage || orb.imageUrl || "";
            let img = fallbackImage;

            if (layerSourceMeta.isAvailable && orb.layers?.length) {
              img = await composeOrbAvatarFromLayers(orb.layers, {
                fallbackImageUrl: fallbackImage,
              });
            }

            if (!img) return null;

            return {
              img,
              name: orb.name || `Infinity Orb #${Number(orb.tokenId) + 1}`,
              level: 0,
              source: "orb",
              tokenId: orb.tokenId,
              layerSource: orb.layerSource || "",
            };
          }),
        )
      ).filter(Boolean);

      if (!alive) return;
      setProfileImageOptions([...composedOrbImages, ...PROFILE_IMAGES]);
    } catch (e) {
      console.error("Failed to load Infinity Orbs:", e);
      setOrbItems([]);
      setProfileImageOptions(PROFILE_IMAGES);
    } finally {
      if (!alive) return;
      setOrbsLoading(false);
      setOrbsLoaded(true);
    }
  };

  loadOrbsIntoProfileImages();

  return () => {
    alive = false;
  };
}, [visible, userData?.id]);



  const nameValid = useMemo(() => isValidUsername(username || ""), [username]);

  const somethingChanged =
    (username || "").trim() !== (userData?.username || "") ||
    imageUrl !== (userData?.imageUrl || "") ||
    imageBg !== (userData?.imageBg || "");

  const handlePick = (type, imgObj) => {
    const { img, level } = imgObj || {};
    const need = parseInt(level, 10) || 0;
    if (canLevel < need) {
      setMessage?.(
        `Reach level ${need} to unlock this ${
          type === "img" ? "profile image" : "background"
        }.`
      );
      setError?.(true);
      return;
    }
    if (type === "img") setImageUrl(img);
    else setImageBg(img);
  };

  const handleSave = async () => {
    try {
      // username checks
      const trimmed = (username || "").trim();
      if (!isValidUsername(trimmed)) {
        setMessage?.(
          "Username invalid. Use 3–20 letters/numbers/underscores, no spaces or profanity."
        );
        setError?.(true);
        return;
      }

      setSaving(true);

      if(nameStatus === 'taken'){
         setMessage?.("Username is already taken.");
          setError?.(true);
          setSaving(false);
          return;
      }

      // uniqueness: only if username changed
      const changedName =
        trimmed.toLowerCase() !== (userData?.username || "").toLowerCase();
      if (changedName) {
        const usersRef = collection(db, "users");
        const snapshot = await getDocs(usersRef);

        const lowerTrimmed = trimmed.toLowerCase();

        const takenByOther = snapshot.docs.find((d) => {
          const data = d.data();
          const uname = (data.username || "").toLowerCase();
          return uname === lowerTrimmed && d.id !== userData.id;
        });

        if (takenByOther) {
          setMessage?.("Username is already taken.");
          setError?.(true);
          setSaving(false);
          return;
        }
      }

      // build updates
      const updates = {};
      if (changedName) {
        updates.username = trimmed;
        // updates.usernameLower = trimmed.toLowerCase();
      }
      if (imageUrl !== (userData?.imageUrl || "")) updates.imageUrl = imageUrl;
      if (imageBg !== (userData?.imageBg || "")) updates.imageBg = imageBg;

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, "users", userData.id), updates);
      }

      setSuccess?.(true);
      setMessage?.("Profile updated!");
      onClose(); // modal closes; the Drawer stays open beneath it
    } catch (err) {
      console.error("Error updating profile:", err);
      setError?.(true);
      setMessage?.("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const GridList = ({ data, kind }) => (
    <Grid
      container
      columns={12}
      spacing={1}
      sx={{
        maxHeight: "40%",
        overflowY: "auto",
        pr: 1,
        pb: 1,
        scrollbarWidth: "none",
        marginBottom: 5,
      }}
    >
      {data.map((item, i) => {
        const locked = canLevel < (item.level || 0);
        const selected =
          kind === "img" ? imageUrl === item.img : imageBg === item.img;

        return (
          <Grid key={`${kind}-${i}`} item xs={3}>
            <Tooltip
              title={
                locked
                  ? `Unlocks at Level ${item.level}`
                  : item.name || "Select"
              }
              placement="top"
            >
              <Box>
                <GridTile
                  locked={locked}
                  selected={selected}
                  label={item.name}
                  img={item.img}
                  onClick={() => handlePick(kind, item)}
                />
              </Box>
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <ModalComponent
      // Raise above Drawer (Drawer uses zIndex modal+1)
      // ModalComponent uses MUI Modal; we bump via md/lg sizing to your spec
      aspectRatio={isNarrow ? "5/8" : "5/6"}
      md={5}
      lg={4}
      ingnorAspect={false}
      modalOpen={visible}
      setModalOpen={onClose}
      padding={0.5}
    >
      {/* Title */}
      {/* <Typography
        variant="h6"
        sx={{
          color: "#fff",
          fontWeight: 900,
          textAlign: "center",
          p: "10px",
          borderRadius: "5px",
          backgroundColor: "#00000080",
          width: "100%",
          height: "fit-content",
        }}
      >
        Edit Profile
      </Typography> */}

      {/* Profile Card (same vibe as Settings Drawer) */}
      <ProfileHeader user={{ ...userData, imageUrl, imageBg }} />

      {/* Username */}
      <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Username"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setNameTouched(true);
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ minWidth: 60, textAlign: "right", pr: 1 }}>
                {nameStatus === "checking" && (
                  <Typography sx={{ fontSize: 12, color: "#6aa4ff" }}>
                    checking…
                  </Typography>
                )}
                {nameStatus === "ok" && (
                  <Typography sx={{ fontSize: 12, color: "#69d46a" }}>
                    available
                  </Typography>
                )}
                {nameStatus === "taken" && (
                  <Typography sx={{ fontSize: 12, color: "#ff6961" }}>
                    taken
                  </Typography>
                )}
              </Box>
            ),
          }}
          sx={{
            flex: 1,
            height: "min-content",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !saving && somethingChanged && nameValid) {
              handleSave();
            }
          }}
        />
        <NeoButton
          text={saving ? "Saving..." : "Save"}
          disabled={saving || !somethingChanged || (nameTouched && !nameValid)}
          color={Theme.palette.primary.main}
          width={28}
          onClick={handleSave}
        />
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          width: "100%",
          height: "fit-content",
          "& .MuiTab-root": { color: "#e2d5ff", fontWeight: 800 },
          "& .Mui-selected": { color: "#ffd24d !important" },
          mb: 1,
        }}
      >
        <Tab label="Profile Image" />
        <Tab label="Profile BG" />
        <Tab
          label={
            <Badge
              color="secondary"
              badgeContent={"soon"}
              sx={{ "& .MuiBadge-badge": { right: -12 } }}
            >
              Badges
            </Badge>
          }
        />
      </Tabs>

      {/* Content */}
      {tab === 0 && <GridList data={profileImageOptions} kind="img" />}
      {tab === 1 && <GridList data={BACKGROUND_IMAGES} kind="bg" />}
      {tab === 2 && (
        <Box
          sx={{
            mt: 1,
            width: "100%",
            height: "40%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#c9b7ff",
            fontWeight: 800,
          }}
        >
          Badges management is coming soon.
        </Box>
      )}

      {/* Bottom buttons (Cancel is provided by ModalComponent footer; add a Save mirror) */}
      <Box
        sx={{
          position: "absolute",
          bottom: "30px",
          right: 0,
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          pr: "16px",
        }}
      >
        <NeoButton
          text={saving ? "Saving..." : "Save"}
          disabled={saving || !somethingChanged || (nameTouched && !nameValid)}
          color={Theme.palette.primary.main}
          width={30}
          onClick={handleSave}
        />
      </Box>
    </ModalComponent>
  );
};

export default EditProfileModal;
